import { useEffect, useId, useRef, useState, type ReactNode } from "react";

/**
 * A small ⓘ affordance that tucks secondary explanation behind a click, so a
 * form stays uncluttered but the "why/what" is one tap away. Mirrors
 * ImportHelp's dismissal a11y (Escape + outside-pointer close, focus returns to
 * the trigger) but is generic: pass a `label` (the accessible name + heading)
 * and any `children` as the body.
 */
export function InfoHint({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  function closeAndRefocus() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  useEffect(() => {
    if (open) popRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") closeAndRefocus();
    }
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (popRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  return (
    <span className={`info-hint${className ? ` ${className}` : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="info-hint-btn"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">ⓘ</span>
      </button>
      {open && (
        <div
          ref={popRef}
          className="info-hint-pop"
          role="dialog"
          aria-labelledby={titleId}
          tabIndex={-1}
        >
          <div className="info-hint-pop-head">
            <strong id={titleId}>{label}</strong>
            <button
              type="button"
              className="info-hint-x"
              aria-label="Close"
              onClick={closeAndRefocus}
            >
              ×
            </button>
          </div>
          <div className="info-hint-body">{children}</div>
        </div>
      )}
    </span>
  );
}
